import { getPlayersPerSide } from '@badminton/matchmaking-core';
import { consumeUpcomingQueueHead } from '../courtUtils';
import { runLocalCourtFill } from '../matchmaking/buildMatchmakingInputV1FromLiveState';
import { applyLocalCourtFill, buildSeatCommandFromFill } from './applyLocalCourtFill';
import { OFFLINE_COMMAND_TYPES } from './offlineQueueConstants';
import { restartCourtMatchTimer } from '../matchTimerUtils';

function emptyUpcomingSlot(idx) {
  return {
    courtNumber: idx,
    team1: [],
    team2: [],
    inProgress: false,
    gameCreation: { origin: null, algorithm: null, isAltered: false, scheduledGameId: null },
  };
}

function rosterIds(list) {
  return (list || []).filter(Boolean);
}

function slotIsFull(slot, playersPerSide) {
  return (slot?.team1?.length || 0) >= playersPerSide && (slot?.team2?.length || 0) >= playersPerSide;
}

function scheduledRosterIds(game) {
  if (Array.isArray(game?.roster_player_ids) && game.roster_player_ids.length > 0) {
    return game.roster_player_ids.filter(Boolean).map(String);
  }
  return [
    game?.team1_player1_id,
    game?.team1_player2_id,
    game?.team2_player1_id,
    game?.team2_player2_id,
  ].filter(Boolean).map(String);
}

function scheduledGameIdFromRow(row) {
  return row?.gameCreation?.scheduledGameId || row?.gameCreation?.scheduled_game_id || null;
}

function findScheduledGameIdsForRoster(scheduledGames, team1, team2) {
  const roster = [...rosterIds(team1), ...rosterIds(team2)].map(String).sort();
  if (roster.length === 0) return [];
  const key = roster.join(',');
  return (scheduledGames || [])
    .filter((game) => {
      if (!game || game.status === 'completed') return false;
      return scheduledRosterIds(game).slice().sort().join(',') === key;
    })
    .map((game) => String(game.id));
}

export function consumeScheduledGamesAfterLocalScore(scheduledGames, { completedIds = [], placedIds = [] } = {}) {
  const completed = new Set((completedIds || []).filter(Boolean).map(String));
  const placed = new Set((placedIds || []).filter(Boolean).map(String));
  return (scheduledGames || [])
    .filter((game) => !completed.has(String(game.id)))
    .map((game) => (placed.has(String(game.id)) ? { ...game, status: 'placed' } : game));
}

/**
 * After a local score, promote next_up if full and refill empty upcoming slots with the local engine.
 */
export function buildLocalPostScoreResult(liveState, { courtNumber, team1, team2, scoreEventId = null }) {
  const playersPerSide = getPlayersPerSide(liveState?.session?.competitive_match_format || 'doubles');
  const finishing = [...rosterIds(team1), ...rosterIds(team2)];
  const nextAssignments = [...(liveState.nextAssignments || [])];
  const firstSlot = nextAssignments[0];
  const promoted = slotIsFull(firstSlot, playersPerSide);
  const promotedFromSlot = promoted ? 0 : null;

  let workingCourts = (liveState.courtAssignments || []).map((court) => {
    if (Number(court.courtNumber) !== Number(courtNumber)) return court;
    const cleared = {
      ...court,
      winner: undefined,
      score: undefined,
      eloDeltas: undefined,
      showEloChange: undefined,
      unbalancedInfo: undefined,
      eloSnapshots: undefined,
    };
    if (promoted && firstSlot) {
      const promotedCourt = {
        ...cleared,
        team1: [...(firstSlot.team1 || [])],
        team2: [...(firstSlot.team2 || [])],
        inProgress: true,
        gameCreation: firstSlot.gameCreation || court.gameCreation,
      };
      restartCourtMatchTimer(promotedCourt);
      return promotedCourt;
    }
    return { ...cleared, team1: [], team2: [], inProgress: false };
  });

  const slotCap = Math.max(
    0,
    Number(liveState.session?.upcoming_queue_slot_count ?? nextAssignments.length) || 0,
  );
  let workingNext = promoted
    ? consumeUpcomingQueueHead(nextAssignments, slotCap, emptyUpcomingSlot)
    : nextAssignments;

  const scoredCourt = (liveState.courtAssignments || []).find(
    (court) => Number(court.courtNumber) === Number(courtNumber)
  );
  const completedScheduledGameIds = [...new Set([
    scheduledGameIdFromRow(scoredCourt),
    ...findScheduledGameIdsForRoster(liveState.scheduledGames, team1, team2),
  ].filter(Boolean).map(String))];
  const placedScheduledGameIds = [...new Set((promoted && firstSlot
    ? [
        scheduledGameIdFromRow(firstSlot),
        ...findScheduledGameIdsForRoster(liveState.scheduledGames, firstSlot.team1, firstSlot.team2),
      ]
    : []
  ).filter(Boolean).map(String).filter((id) => !completedScheduledGameIds.includes(id)))];
  const scheduledGames = consumeScheduledGamesAfterLocalScore(liveState.scheduledGames, {
    completedIds: completedScheduledGameIds,
    placedIds: placedScheduledGameIds,
  });

  const waitingList = [
    ...(liveState.waitingList || []).filter((id) => !finishing.includes(id)),
    ...finishing,
  ];

  const pendingFinishGame = { team1: rosterIds(team1), team2: rosterIds(team2) };
  const playersById = { ...(liveState.playersById || {}) };
  finishing.forEach((id) => {
    const player = playersById[id];
    if (!player) return;
    const played = (player.gamesPlayedThisSession ?? 0) + 1;
    playersById[id] = {
      ...player,
      gamesPlayedThisSession: played,
      effectiveGamesPlayed: (player.effectiveGamesPlayed ?? player.gamesPlayedThisSession ?? 0) + 1,
    };
  });

  const fillState = {
    ...liveState,
    playersById,
    courtAssignments: workingCourts,
    nextAssignments: workingNext,
    waitingList,
    scheduledGames,
    excludedPlayerIds: finishing,
    pendingFinishGame,
    matchHistory: [
      ...(liveState.matchHistory || []),
      { gameId: 'pending-finish', team1: pendingFinishGame.team1, team2: pendingFinishGame.team2 },
    ],
    totalGamesCompleted: (liveState.totalGamesCompleted ?? (liveState.matchHistory || []).length) + 1,
  };

  const emptyIndexes = workingNext
    .map((slot, idx) => (slotIsFull(slot, playersPerSide) ? null : idx))
    .filter((idx) => idx != null);

  const fills = [];
  if (emptyIndexes.length > 0) {
    try {
      const local = runLocalCourtFill(
        fillState,
        emptyIndexes.map((index) => ({ court_type: 'next_up', index }))
      );
      if (local.ok) {
        (local.fills || []).forEach((fill) => {
          const index = fill.slotIndex ?? fill.targetIndex;
          if (index == null) return;
          fills.push(fill);
          const applied = applyLocalCourtFill(
            { courtAssignments: workingCourts, nextAssignments: workingNext, waitingList: fillState.waitingList },
            fill,
            { courtType: 'next_up', index }
          );
          workingNext = applied.nextAssignments;
          fillState.waitingList = applied.waitingList;
          fillState.nextAssignments = workingNext;
        });
      }
    } catch (error) {
      console.warn('[localPostScoreFill] Upcoming refill failed; keeping the local score:', error);
    }
  }

  return {
    promoted,
    promotedFromSlot,
    fills,
    courtAssignments: workingCourts,
    nextAssignments: workingNext,
    waitingList: fillState.waitingList,
    scheduledGames,
    completedScheduledGameIds,
    placedScheduledGameIds,
    seatCommands: fills.map((fill) =>
      buildSeatCommandFromFill(fill, {
        courtType: 'next_up',
        index: fill.slotIndex ?? fill.targetIndex,
        overwriteOwnRefill: true,
        causedByScoreEventId: scoreEventId,
      })
    ),
  };
}

export function buildScoreCommand(payload) {
  return {
    type: OFFLINE_COMMAND_TYPES.SCORE,
    ...payload,
  };
}
